import './methodologies-cards.scss';

import AnimatedVisualSwap from "@frontend/js/Components/Shared/Animation/AnimatedVisualSwap";
import ArrowNextBigIcon from '@frontend/images/icons/ArrowNextBigIcon';
import { resolvePreferredLocale } from '@frontend/js/utils/locale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, usePage } from '@inertiajs/react';


function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function smoothstep(value) {
    return value * value * (3 - 2 * value);
}

const STACK_BREAKPOINT = 1250;

export default function MethodologiesCards({
    cards = [],
    stackOnScroll = false,
}) {
    const { props, url } = usePage();
    const locale = resolvePreferredLocale({
        url,
        currentLocale: props?.currentLocale,
        siteLanguages: props?.siteLanguages,
    });
    const rootRef = useRef(null);
    const [isWide, setIsWide] = useState(
        typeof window !== "undefined"
            ? window.innerWidth >= STACK_BREAKPOINT
            : true,
    );

    useEffect(() => {
        const handleResize = () => {
            setIsWide(window.innerWidth >= STACK_BREAKPOINT);
        };
        window.addEventListener("resize", handleResize, { passive: true });
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const effectiveStackOnScroll = stackOnScroll && isWide;
    const [fadeStrength, setFadeStrength] = useState(1);
    const cardCount = cards.length;
    const stickyTopBase = 96;
    const stickyTopStep = 12;

    useEffect(() => {
        if (!effectiveStackOnScroll || cardCount < 2 || !rootRef.current) {
            setFadeStrength(1);
            return undefined;
        }

        let frameId = null;

        const updateFade = () => {
            if (!rootRef.current) {
                return;
            }

            const rect = rootRef.current.getBoundingClientRect();
            const sectionProgress = clamp(
                (window.innerHeight - rect.top) / (rect.height + window.innerHeight),
                0,
                1,
            );
            const fadeStart = 0.35;
            const fadeEnd = 0.9;
            const normalizedProgress = clamp(
                (sectionProgress - fadeStart) / (fadeEnd - fadeStart),
                0,
                1,
            );
            const nextFadeStrength = 1 - smoothstep(normalizedProgress);

            setFadeStrength((prev) =>
                Math.abs(prev - nextFadeStrength) < 0.01
                    ? prev
                    : nextFadeStrength,
            );
        };

        const handleScroll = () => {
            if (frameId) {
                return;
            }

            frameId = window.requestAnimationFrame(() => {
                updateFade();
                frameId = null;
            });
        };

        updateFade();
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [effectiveStackOnScroll, cardCount]);

    const cardsWithProgress = useMemo(
        () => cards.map((card, index) => ({
            card,
            index,
            stickyTop: `${stickyTopBase + index * stickyTopStep}px`,
        })),
        [cards],
    );

    const showStackFade =
        effectiveStackOnScroll && cardCount > 1 && fadeStrength > 0.02;

    const rootClassName = effectiveStackOnScroll
        ? "methodologiesCards methodologiesCards--stacked"
        : "methodologiesCards";
    const cardBaseClass = "methodologiesCards__stackViewport__stackTrack__card";

    return (
        <div
            ref={rootRef}
            className={rootClassName}
            style={
                effectiveStackOnScroll
                    ? {
                          "--cards-count": cardCount,
                          "--stack-fade-strength": fadeStrength,
                      }
                    : undefined
            }
        >
            <div className="methodologiesCards__stackViewport">
                <div className="methodologiesCards__stackViewport__stackTrack">
                    {cardsWithProgress.map(
                        ({ card, index, stickyTop }) => {
                            const href = card.href || "#";

                            return (
                                <Link
                                    key={card.id || index}
                                    href={href}
                                    className={cardBaseClass}
                                    style={
                                        effectiveStackOnScroll
                                            ? {
                                                  "--card-index": index,
                                                  "--stack-card-top": stickyTop,
                                              }
                                            : undefined
                                    }
                                >
                                    <div
                                        className={`${cardBaseClass}__content`}
                                    >
                                        <h3>{card.title}</h3>
                                        <p>{card.description}</p>
                                        <div
                                            className={`${cardBaseClass}__content__tags`}
                                        >
                                            {(card.tags || []).map((tag) => (
                                                <p key={`${card.id}-${tag}`}>
                                                    {tag}
                                                </p>
                                            ))}
                                        </div>

                                        {card.note && (
                                            <p
                                                className={`${cardBaseClass}__note`}
                                            >
                                                {card.note}
                                            </p>
                                        )}
                                    </div>

                                    <div className={`${cardBaseClass}__visual`}>
                                        {card.animation_id ? (
                                            <AnimatedVisualSwap
                                                animationId={card.animation_id}
                                                type="methodology"
                                            />
                                        ) : null}
                                        <div
                                            className={`${cardBaseClass}__visual__arrow`}
                                        >
                                            <span
                                                className={`${cardBaseClass}__visual__arrow__icon ${cardBaseClass}__visual__arrow__icon--first`}
                                                aria-hidden="true"
                                            >
                                                <ArrowNextBigIcon />
                                            </span>
                                            <span
                                                className={`${cardBaseClass}__visual__arrow__icon ${cardBaseClass}__visual__arrow__icon--second`}
                                                aria-hidden="true"
                                            >
                                                <ArrowNextBigIcon />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            );
                        },
                    )}
                </div>
                {showStackFade && (
                    <div
                        className="methodologiesCards__stackFade"
                        aria-hidden="true"
                    />
                )}
            </div>
        </div>
    );
}
